import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download, XCircle, Terminal } from 'lucide-react';
import type { PipelineEvent, Run, Stage } from '../../../shared/types';
import { useCollection, useRunLog } from '../lib/hooks';
import { cancelRun, downloadRunLog, errorText } from '../lib/api';
import { confirmDialog } from './ConfirmDialog';
import { formatDuration, parseDate } from '../lib/pb';

const LEVEL_STYLE: Record<string, string> = {
  debug: 'text-natural-text/40',
  info: 'text-natural-text/75',
  warn: 'text-amber-700',
  error: 'text-red-600',
};

/**
 * The live run log for a campaign.
 *
 * Events are written by the worker the instant each step happens, so this both
 * tails in real time and remains complete after a cancel or a crash — the thing
 * n8n couldn't do, because it only serialised execution data at the end and a
 * cancelled execution therefore had nothing to show.
 */
export default function RunTimeline({ campaignId, stage }: { campaignId: string; stage: Stage }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runs = useCollection<Run>('runs', {
    filter: `campaign = "${campaignId}"`,
    sort: '-run_number',
  });

  const latest = runs.items[0] ?? null;
  const events = useRunLog(open && latest ? latest.id : null);

  // Auto-open while something is happening, so progress is visible without a click.
  useEffect(() => {
    if (latest?.status === 'running') setOpen(true);
  }, [latest?.status]);

  const elapsed = useMemo(() => {
    if (!latest) return null;
    const started = parseDate(latest.started);
    if (!started) return null;
    const ended = parseDate(latest.ended) ?? new Date();
    return formatDuration(ended.getTime() - started.getTime());
  }, [latest, events.items.length]);

  if (!latest) return null;

  const live = latest.status === 'running' || latest.status === 'cancelling';

  return (
    <div className="zen-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 p-4">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left cursor-pointer min-w-0"
        >
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          <Terminal size={14} className="text-natural-text/40 shrink-0" />
          <span className="text-xs font-semibold truncate">
            Run #{latest.run_number}
            <span className="font-normal text-natural-text/50">
              {' '}
              · {latest.status}
              {elapsed ? ` · ${elapsed}` : ''}
              {runs.items.length > 1 ? ` · ${runs.items.length} runs total` : ''}
            </span>
          </span>
          {live && (
            <span className="h-1.5 w-1.5 rounded-full bg-natural-sage animate-pulse shrink-0" />
          )}
        </button>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={async () => {
              try {
                await downloadRunLog(latest.id, `run-${latest.run_number}.txt`);
              } catch (err) {
                setError(errorText(err));
              }
            }}
            className="zen-btn-ghost py-1.5 px-2.5 text-[11px]"
            title="Download this run's log as a text file"
          >
            <Download size={12} />
            <span className="hidden sm:inline">Log</span>
          </button>

          {live && (
            <button
              onClick={async () => {
                if (
                  !(await confirmDialog({
                    title: 'Cancel this run?',
                    message: 'Work already finished is kept — only the remaining steps stop.',
                    confirmLabel: 'Cancel the run',
                    cancelLabel: 'Keep running',
                    danger: true,
                  }))
                ) {
                  return;
                }
                setBusy(true);
                setError(null);
                try {
                  await cancelRun(latest.id);
                  await runs.reload();
                } catch (err) {
                  setError(errorText(err));
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="zen-btn-danger py-1.5 px-2.5 text-[11px]"
            >
              <XCircle size={12} />
              {busy ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
        </div>
      </div>

      {error && <p className="px-4 pb-3 text-xs text-red-600">{error}</p>}

      {open && (
        <div className="border-t border-natural-border bg-stone-50/60">
          {latest.error && (
            <div className="px-4 py-3 border-b border-natural-border bg-red-50/60 text-xs text-red-700">
              {latest.error}
            </div>
          )}

          <div className="max-h-80 overflow-y-auto font-mono text-[11px] leading-relaxed p-4 space-y-0.5">
            {events.loading && <p className="text-natural-text/40">Loading log…</p>}
            {!events.loading && events.items.length === 0 && (
              <p className="text-natural-text/40">No events recorded yet.</p>
            )}
            {(events.items as PipelineEvent[]).map((ev) => (
              <LogLine key={ev.id} event={ev} />
            ))}
          </div>

          {stage === 'needs_attention' && (
            <p className="px-4 py-3 border-t border-natural-border text-[11px] text-amber-700">
              This campaign stopped partway. The log above shows exactly where.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function LogLine({ event }: { event: PipelineEvent }) {
  const [expanded, setExpanded] = useState(false);
  const time = parseDate(event.created);
  const hasDetail = event.detail && Object.keys(event.detail).length > 0;

  return (
    <div>
      <button
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className={`w-full text-left flex gap-2 ${hasDetail ? 'cursor-pointer hover:bg-white/70 rounded' : 'cursor-default'}`}
      >
        <span className="text-natural-text/35 shrink-0 tabular-nums">
          {time
            ? time.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })
            : '--:--:--'}
        </span>
        <span className={`shrink-0 w-10 ${LEVEL_STYLE[event.level] ?? ''}`}>
          {event.level.toUpperCase()}
        </span>
        <span className="text-natural-sage shrink-0 w-24 truncate">{event.stage}</span>
        <span className={`min-w-0 break-words ${LEVEL_STYLE[event.level] ?? ''}`}>
          {event.message}
          {event.duration_ms ? (
            <span className="text-natural-text/35"> ({(event.duration_ms / 1000).toFixed(2)}s)</span>
          ) : null}
        </span>
      </button>

      {expanded && hasDetail && (
        <pre className="ml-[4.5rem] my-1 p-2 bg-white border border-natural-border rounded text-[10px] overflow-x-auto whitespace-pre-wrap break-words">
          {JSON.stringify(event.detail, null, 2)}
        </pre>
      )}
    </div>
  );
}
